import type { CityConfig } from "../city-template/types";

export const camdenConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Camden",
  state: "Maine",
  stateAbbr: "ME",
  slug: "camden",
  route: "/cities/camden",
  title: "Emergency Passport Camden, ME — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Camden, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Camden, Rockport, Lincolnville, Hope, and Knox County. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Camden, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Camden and mid-coast Maine travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Camden%20Maine%20harbor%20with%20scenic%20windjammer%20schooners%20and%20mountain%20backdrop%2C%20Penobscot%20Bay%20with%20historic%20waterfront%20and%20village%20shops%2C%20mid-coast%20Maine%20with%20Camden%20Hills%20and%20sailboats%2C%20classic%20New%20England%20harbor%20with%20rolling%20green%20hills%20and%20rocky%20coastline%2C%20summer%20afternoon%20light%2C%20professional%20travel%20photography%2C%20wide%20angle%20scenic%20view%20of%20Camden%20Maine%20harbor&width=1800&height=700&seq=camden-me-hero-001&orientation=landscape",
  heroImageAlt: "Camden Maine scenic harbor with windjammer schooners and Camden Hills",
  geoPosition: "44.2098;-69.0648",
  geoPlacename: "Camden, Maine",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 180 miles (3.5 hours) from Camden, ME",
  },
  nearbyCitiesZips: [
    { name: "Camden", zips: "04843" },
    { name: "Rockport", zips: "04856" },
    { name: "Lincolnville", zips: "04849" },
    { name: "Hope", zips: "04847" },
    { name: "Appleton", zips: "04862" },
    { name: "Searsmont", zips: "04973" },
    { name: "Belfast", zips: "04915" },
    { name: "Northport", zips: "04849" },
    { name: "Waldo", zips: "04915" },
    { name: "Union", zips: "04862" },
    { name: "Warren", zips: "04864" },
    { name: "Thomaston", zips: "04861" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Camden departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Rockland", route: "/cities/rockland" },
    { name: "Emergency passport help in Belfast", route: "/cities/belfast" },
    { name: "Urgent passport services in Portland", route: "/cities/portland-me" },
    { name: "Same-day passport help in Augusta", route: "/cities/augusta-me" },
    { name: "Expedited passport services in Bangor", route: "/bangor" },
    { name: "Passport courier services in Ellsworth", route: "/cities/ellsworth" },
    { name: "Rush passport help in Bar Harbor", route: "/cities/bar-harbor" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Camden, Maine remotely via secure courier. We do not have a walk-in office in Camden — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Camden travelers.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Serving the greater Camden and mid-coast Maine area",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Camden and mid-coast Maine. From the scenic harbor with its iconic windjammer fleet to the Camden Hills and the charming villages of Rockport and Lincolnville — we help Camden travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Camden neighborhoods and nearby towns including Rockport, Lincolnville, Hope, Appleton, Searsmont, Belfast, Northport, Waldo, Union, Warren, and Thomaston.",
  howItWorksIntro: "Most Camden travelers complete everything online and ship documents overnight. No need to drive to Boston or Portland. Our emergency and expedited passport process is designed for Maine coastal travelers who need fast, reliable results.",
  mistakesIntro: "Camden is a beautiful harbor town with a thriving arts and sailing community. Residents and seasonal visitors often need passports for international travel. Here are the most common mistakes we see from Camden-area travelers.",
  testimonialsIntro: "Camden travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Camden? Here are the most common ones we hear from mid-coast Maine travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  dmvWebsite: "https://www.maine.gov/sos/bmv",
  dmvLabel: "Maine BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Camden international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Patricia W.",
      location: "Camden, ME",
      rating: 5,
      text: "Sailing trip to the Caribbean and my passport expired 3 weeks before departure. Fast Passport Center had it renewed in 5 days. The overnight shipping from our Camden address was seamless. Outstanding service.",
      service: "Passport Renewal",
    },
    {
      name: "James R.",
      location: "Rockport, ME",
      rating: 5,
      text: "Emergency business trip to Germany. Needed my passport in 48 hours. Fast Passport Center delivered exactly on time. They even coordinated the pickup with the local Rockport Post Office.",
      service: "Emergency Passport",
    },
    {
      name: "Susan T.",
      location: "Lincolnville, ME",
      rating: 5,
      text: "Lost my passport during a weekend visit to Camden. Fast Passport Center helped me with the DS-64 form and police report. Had my replacement in 3 days. I was amazed at how smooth it was.",
      service: "Lost Passport",
    },
    {
      name: "Emily K.",
      location: "Camden, ME",
      rating: 5,
      text: "Passport renewal for my daughter's study abroad program in Ireland. Fast Passport Center guided us through the dual-parent consent requirements and got the passport in 6 days. Couldn't have asked for better.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "camden-1",
      name: "Camden Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "25 Chestnut St",
      city: "Camden",
      state: "ME",
      zip: "04843",
      phone: "(207) 236-3569",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Camden harbor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Main passport acceptance facility for Camden and mid-coast area. Call ahead for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "camden-2",
      name: "Rockport Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2 Russell Ave",
      city: "Rockport",
      state: "ME",
      zip: "04856",
      phone: "(207) 236-8333",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2 miles from Camden",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Rockport and nearby Lincolnville residents.",
      icon: "ri-mail-line",
    },
    {
      id: "camden-3",
      name: "Rockland Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "270 Main St",
      city: "Rockland",
      state: "ME",
      zip: "04841",
      phone: "(207) 594-2255",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles from Camden",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Larger facility with more appointment slots. Good backup option.",
      icon: "ri-mail-line",
    },
    {
      id: "camden-4",
      name: "Bangor Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "25 Pendleton St",
      city: "Bangor",
      state: "ME",
      zip: "04401",
      phone: "(207) 945-3611",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "65 miles from Camden",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest facility in the region. More appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "camden-5",
      name: "Camden Town Office",
      type: "Municipal Passport Acceptance",
      address: "29 Elm St",
      city: "Camden",
      state: "ME",
      zip: "04843",
      phone: "(207) 236-7955",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "In downtown Camden",
      website: "https://www.camdentownoffice.com/",
      websiteLabel: "Camden Town Office",
      notes: "Town clerk services. Call ahead to confirm passport acceptance hours.",
      icon: "ri-government-line",
    },
    {
      id: "camden-6",
      name: "Augusta Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "40 Western Ave",
      city: "Augusta",
      state: "ME",
      zip: "04330",
      phone: "(207) 622-3139",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "50 miles from Camden",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "State capital facility. Backup option for mid-coast area.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local Camden photo shops may use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Camden acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Camden applicants show up with incorrect checks and have to reschedule.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Camden families.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Camden travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location near Camden." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Camden address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Camden, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Camden in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays.",
    },
    {
      q: "Can I get a same-day passport in Camden, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option.",
    },
    {
      q: "Where can I get a fast passport renewal in Camden, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Camden with no office visit required. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "How far is Camden from the Boston Passport Agency?",
      a: "Camden is approximately 180 miles from the Boston Passport Agency — about a 3.5-hour drive. With Fast Passport Center, you avoid that trip entirely.",
    },
    {
      q: "What acceptance facilities are near Camden, Maine?",
      a: "Options near Camden include the Camden Post Office (04843), Rockport Post Office (04856), Rockland Post Office (04841), and Camden Town Office (04843). The Camden Post Office is most convenient.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery.",
    },
    {
      q: "Is Camden near any airports?",
      a: "Camden is about 65 miles from Bangor International Airport (BGR) and about 80 miles from Portland International Jetport (PWM). Knox County Regional Airport (RKD) in Rockland is about 5 miles away with small regional flights.",
    },
    {
      q: "How much does expedited passport service cost in Camden?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a quote.",
    },
    {
      q: "What documents do I need for a first-time passport in Camden?",
      a: "You'll need proof of citizenship, government-issued photo ID, two passport photos, and payment. We provide a complete checklist and review your documents.",
    },
    {
      q: "Can you help with a child's passport in Camden?",
      a: "Yes. Child passports require both parents (or notarized consent), the child's birth certificate, and both parents' IDs. We walk Camden families through the process.",
    },
    {
      q: "Is there a UPS Store near Camden for shipping documents?",
      a: "The nearest UPS drop-off is in Rockland (5 miles). We provide a prepaid overnight label. The Camden Post Office on Chestnut St also accepts packages.",
    },
    {
      q: "Can I get passport photos taken in Camden?",
      a: "Yes, CVS on Elm St and the Camden Rite Aid offer passport photo services. Walgreens in Rockland and Belfast are also options.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Camden", address: "115 Elm St, Camden, ME 04843", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Rite Aid — Camden", address: "25 Chestnut St, Camden, ME 04843", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/" },
    { name: "CVS Pharmacy — Rockland", address: "75 Park St, Rockland, ME 04841", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Rite Aid — Rockland", address: "270 Main St, Rockland, ME 04841", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/" },
    { name: "Walgreens — Belfast", address: "30 Main St, Belfast, ME 04915", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "CVS Pharmacy — Bangor", address: "40 Main St, Bangor, ME 04401", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walmart Photo Center — Bangor", address: "60 Skyway Dr, Bangor, ME 04401", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walmart.com/" },
    { name: "Rite Aid — Augusta", address: "25 Western Ave, Augusta, ME 04330", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/" },
  ],
  dmvLocations: [
    {
      name: "BMV — Rockland",
      address: "25 Water St, Rockland, ME 04841",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Knox County BMV. 5 miles from Camden. Online appointment booking available.",
      featured: true,
    },
    {
      name: "BMV — Belfast",
      address: "66 Union St, Belfast, ME 04915",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Waldo County option. 30 miles from Camden.",
      featured: false,
    },
    {
      name: "BMV — Bangor",
      address: "45 Skyway Dr, Bangor, ME 04401",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service BMV. 65 miles from Camden.",
      featured: false,
    },
    {
      name: "BMV — Augusta",
      address: "29 State House Station, Augusta, ME 04333",
      hours: "Mon–Fri: 8AM–5PM",
      note: "State capital full-service BMV. 50 miles from Camden.",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Maine DHHS - Vital Records", desc: "Birth, death, marriage, divorce records for the state of Maine.", url: "https://www.maine.gov/dhhs/mecdc/public-health-systems/data-research/vital-records/" },
    { icon: "ri-government-line", label: "Knox County Clerk", desc: "Marriage licenses and divorce decrees for Knox County residents.", url: "https://www.knoxcountymaine.gov/" },
    { icon: "ri-global-line", label: "VitalChek - Online Vital Records", desc: "Order official birth, death, marriage certificates online with expedited delivery.", url: "https://www.vitalchek.com/" },
  ],
  airports: [
    {
      code: "RKD",
      name: "Knox County Regional Airport",
      distance: "5 miles from Camden (10 min)",
      address: "82 Airport Rd, Owls Head, ME 04854",
      note: "Small regional airport in Owls Head with seasonal flights to Boston by Cape Air. Limited commercial service but very close to Camden.",
      flightUrl: "https://www.knoxcountyairport.com/",
      flightLabel: "Knox County Airport",
      featured: false,
    },
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "65 miles from Camden (70 min)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Closest major airport to Camden with direct flights to Boston, New York, Philadelphia, and seasonal Florida routes.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: true,
    },
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "80 miles from Camden (85 min)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's largest airport with the most flight options. Good for international connections.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: false,
    },
  ],
};